import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import StudioCanvas from "./StudioCanvas";
import StudioLayers from "./StudioLayers";
import StudioPageName from "./StudioPageName";
import { styleGroupIds, styleGroupName, styleGroupKeys, createStyleGroup } from "./studioStyleGroups";
import { Button, Icon, PanelSection, Field } from "./StudioControls";
import { uid } from "./studioModel";
import { download, textLayoutReport } from "./studioExport";
import { fitLinkedText } from "./studioLinkedText";
import StudioMaintenance from "./StudioMaintenance";
export default function StudioLibrary({
  tab,
  activeStyleGroup,
  selectStyleGroup,
  doc,
  page,
  content,
  selected,
  setSelected,
  setPageId,
  commit,
  add,
  uploadRef,
  patch,
  focusedLayerId,
  moveLayer,
  group,
  ungroup,
  setScope,
  maintenance,
}) {
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [includeSelection, setIncludeSelection] = useState(true);
  const [groupError, setGroupError] = useState("");
  const selectedTextCount = doc.pages.flatMap((p) => p.elements).filter((n) => n.type === "text" && selected.includes(n.id)).length;
  const [query, setQuery] = useState("");
  const [spacingReport, setSpacingReport] = useState(null);
  useEffect(() => setSpacingReport(null), [doc, content]);
  const libraryRef = useRef(null);
  const activePageRef = useRef(null);
  const previousTabRef = useRef(null);
  const pageIndex = doc.pages.findIndex((p) => p.id === page.id);
  useLayoutEffect(() => {
    const openingPages = previousTabRef.current !== "pages";
    previousTabRef.current = tab;
    if (tab !== "pages") return;
    const library = libraryRef.current;
    const card = activePageRef.current;
    if (!library || !card) return;
    const viewport = library.getBoundingClientRect();
    const thumbnail = card.getBoundingClientRect();
    if (thumbnail.top < viewport.top || thumbnail.bottom > viewport.bottom) {
      // Scroll only the library; scrollIntoView can also move the editor canvas.
      library.scrollTo({
        top:
          library.scrollTop +
          thumbnail.top -
          viewport.top -
          Math.max(0, (viewport.height - thumbnail.height) / 2),
        behavior:
          openingPages ||
          window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
            ? "instant"
            : "smooth",
      });
    }
  }, [page.id, pageIndex, tab]);
  return (
    <aside id="ms-library-panel" className="ms-library" ref={libraryRef}>
      <div className="ms-panel-heading">
        <h2>
          {
            {
              pages: "Pages",
              layers: "Layers",
              text: "Text & content",
              shapes: "Elements",
              image: "Artwork",
              styles: "Design system",
            }[tab]
          }
        </h2>
        <span className="ms-kicker">
          {tab === "pages" ? `${doc.pages.length} TOTAL` : "LIBRARY"}
        </span>
      </div>
      {tab === "pages" && (
        <>
          <div className="ms-page-list">
            {doc.pages.map((p, index) => (
              <div
                key={p.id}
                ref={p.id === page.id ? activePageRef : undefined}
                className={`ms-page-card ${
                  p.id === page.id ? "is-active" : ""
                }`}
              >
                <div className="ms-page-card-label">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <StudioPageName
                    page={p}
                    number={index + 1}
                    location="pages list"
                    onRename={(name) =>
                      commit((d) => ({
                        ...d,
                        pages: d.pages.map((candidate) =>
                          candidate.id === p.id
                            ? { ...candidate, name }
                            : candidate
                        ),
                      }))
                    }
                  />
                </div>
                <button
                  className="ms-page-preview"
                  onClick={() => {
                    setPageId(p.id);
                    setSelected([]);
                  }}
                  aria-label={`Open ${p.name}`}
                >
                  <StudioCanvas
                    doc={doc}
                    page={p}
                    content={content}
                    zoom={Math.min(156 / p.width, 190 / p.height)}
                    preview
                  />
                </button>
                {p.id === page.id && (
                  <Button
                    icon="trash"
                    className="ms-page-delete"
                    title="Delete page (undo available)"
                    disabled={doc.pages.length === 1}
                    onClick={() => {
                      commit((d) => ({
                        ...d,
                        pages: d.pages.filter((v) => v.id !== p.id),
                      }));
                      setSelected([]);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="ms-library-bottom">
            <Button
              icon="plus"
              className="ms-wide"
              disabled={doc.pages.length >= 40}
              onClick={() => {
                const next = {
                  ...page,
                  id: uid(),
                  name: `Page ${doc.pages.length + 1}`,
                  elements: [],
                };
                commit((d) => ({ ...d, pages: [...d.pages, next] }));
                setPageId(next.id);
                setSelected([]);
              }}
            >
              Add blank page
            </Button>
          </div>
        </>
      )}
      {tab === "layers" && <StudioLayers focusedLayerId={focusedLayerId} page={page} selected={selected} setSelected={setSelected} patch={patch} moveLayer={moveLayer} commit={commit} group={group} ungroup={ungroup} />}
      {tab === "text" && (
        <>
          <PanelSection title="Add to your menu">
            <button
              className="ms-type-sample ms-sample-title"
              onClick={() => add("text", "title", "Trattoria Demi")}
            >
              Add a title <span>29 pt</span>
            </button>
            <button
              className="ms-type-sample ms-sample-section"
              onClick={() => add("text", "section", "New section")}
            >
              Add a section <span>16 pt</span>
            </button>
            <button
              className="ms-type-sample"
              onClick={() => add("text", "dish", "Dish name")}
            >
              Add a dish name <span>10.5 pt</span>
            </button>
            <button
              className="ms-type-sample ms-sample-description"
              onClick={() =>
                add(
                  "text",
                  "description",
                  "Ingredients, provenance, a little story."
                )
              }
            >
              Add a description
            </button>
            <Button
              icon="plus"
              className="ms-wide"
              onClick={() => add("text", "note", "A note for your guests")}
            >
              Add a note
            </Button>
          </PanelSection>
          <PanelSection title="Connected content">
            <p className="ms-hint">
              Add a fresh linked field from the CMS. Source changes appear
              whenever the menu is loaded.
            </p>
            <Field label="Find CMS text">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search dishes, sections…"
              />
            </Field>
            <div className="ms-content-list">
              {Object.entries(content)
                .filter(
                  ([key, value]) =>
                    value &&
                    `${value} ${key}`
                      .toLowerCase()
                      .includes(query.toLowerCase())
                )
                .slice(0, 100)
                .map(([binding, text]) => (
                  <button
                    key={binding}
                    onClick={() =>
                      add(
                        "text",
                        binding.endsWith(":header")
                          ? "section"
                          : binding.endsWith(":price")
                          ? "price"
                          : binding.endsWith(":name")
                          ? "dish"
                          : "description",
                        text,
                        {
                          binding,
                          sectionId: binding.startsWith("section:")
                            ? binding.split(":")[1]
                            : undefined,
                        }
                      )
                    }
                  >
                    <Icon name="link" size={13} />
                    <span>
                      {text}
                      <small>{binding.split(":").pop()}</small>
                    </span>
                  </button>
                ))}
            </div>
          </PanelSection>
        </>
      )}
      {tab === "shapes" && (
        <>
          <PanelSection title="The finishing touches">
            <div className="ms-shape-grid">
              {[
                ["rect", "Rectangle"],
                ["ellipse", "Ellipse"],
                ["line", "Fine rule"],
              ].map(([type, name]) => (
                <button
                  key={type}
                  onClick={() => add(type, "decoration", "", { name })}
                >
                  <span className={`ms-shape-example ms-example-${type}`} />
                  {name}
                </button>
              ))}
              <button
                onClick={() =>
                  add("text", "decoration", "❧", {
                    name: "Botanical ornament",
                    w: 100,
                    h: 60,
                    style: {
                      fontFamily: "Georgia",
                      fontSize: 48,
                      align: "center",
                    },
                  })
                }
              >
                <span className="ms-ornament">❧</span>Ornament
              </button>
            </div>
          </PanelSection>
          <PanelSection title="Menu details">
            <Button
              className="ms-wide"
              onClick={() =>
                add(
                  "text",
                  "note",
                  "Please inform your server of any food allergies."
                )
              }
            >
              Allergy note
            </Button>
            <Button
              className="ms-wide"
              onClick={() =>
                add(
                  "text",
                  "note",
                  "Vegetarian · Gluten-free options available"
                )
              }
            >
              Dietary legend
            </Button>
            <Button
              className="ms-wide"
              onClick={() => add("text", "subtitle", "SEASONAL SPECIALS")}
            >
              Seasonal label
            </Button>
          </PanelSection>
        </>
      )}
      {tab === "image" && (
        <>
          <PanelSection title="Your restaurant, your artwork">
            <div className="ms-upload-card">
              <Icon name="image" size={32} />
              <h3>Bring your own artwork</h3>
              <p>
                Logos, illustrations, photographs.
                <br />
                PNG, JPEG or WebP · up to 2 MB
              </p>
              <Button
                icon="plus"
                className="ms-primary"
                onClick={() => uploadRef.current.click()}
              >
                Upload image
              </Button>
            </div>
            <p className="ms-hint">
              Transparent PNGs work beautifully for logos. Keep large artwork at
              300 pixels per printed inch.
            </p>
          </PanelSection>
          <PanelSection title="In this document">
            <div className="ms-assets">
              {Array.from(
                new Map(
                  doc.pages
                    .flatMap((p) => p.elements)
                    .filter((n) => n.type === "image")
                    .map((n) => [n.src, n])
                ).values()
              ).map((n) => (
                <button
                  key={n.id}
                  onClick={() =>
                    add("image", "decoration", "", {
                      src: n.src,
                      artworkSource: n.artworkSource,
                      artworkTransform: n.artworkTransform,
                      name: n.name,
                      w: n.w,
                      h: n.h,
                    })
                  }
                >
                  <img src={n.src} alt={n.name} />
                  <span>{n.name}</span>
                </button>
              ))}
            </div>
          </PanelSection>
        </>
      )}
      {tab === "styles" && (
        <>
          <PanelSection title="Layout review">
            <Button className="ms-wide" onClick={async () => {
              await document.fonts?.ready;
              commit((d) => {
                let fitted = fitLinkedText(d, content, { connect: true });
                // Measure actual rendered glyphs after fitting; clear residual
                // collisions without changing typography or item groups.
                for (let pass = 0; pass < 8; pass++) {
                  const reports = textLayoutReport(fitted, content);
                  if (!reports.some((p) => p.overlaps.length)) break;
                  fitted = { ...fitted, pages: fitted.pages.map((p) => {
                    const report = reports.find((r) => r.id === p.id), shifts = new Map();
                    report?.overlaps.forEach(([a, b]) => {
                      const pair = [a, b].map((id) => report.nodes.find((n) => n.id === id)).sort((a, b) => a.y - b.y);
                      const lower = p.elements.find((n) => n.id === pair[1].id);
                      if (!lower?.locked) shifts.set(lower.id, Math.max(shifts.get(lower.id) || 0, pair[0].y + pair[0].h + 3 - pair[1].y));
                    });
                    return { ...p, elements: p.elements.map((n) => shifts.has(n.id) ? { ...n, y: n.y + shifts.get(n.id) } : n) };
                  }) };
                }
                return fitted;
              });
            }}>Fit item names &amp; inline prices</Button>
            <Button className="ms-wide" onClick={async () => {
              await document.fonts?.ready;
              setSpacingReport(textLayoutReport(doc, content));
            }}>Check text spacing</Button>
            {spacingReport && <>
              <p className="ms-hint" role="status">{spacingReport.some((p) => p.overlaps.length)
                ? "Possible text overlaps found. Select a page to inspect."
                : `No text overlaps found across ${spacingReport.length} pages.`}</p>
              {spacingReport.filter((p) => p.overlaps.length).map((p) => <button className="ms-role-row" key={p.id} onClick={() => { setPageId(p.id); setSelected(p.overlaps[0]); }}><span>{p.name}</span><b>{p.overlaps.length}</b></button>)}
              <Button className="ms-wide" onClick={() => download(new Blob([JSON.stringify(spacingReport)], { type: "application/json" }), `menu-spacing-${doc.menuKey}.json`)}>Download text spacing report</Button>
            </>}
          </PanelSection>
          <StudioMaintenance {...maintenance} doc={doc} page={page} content={content} setPageId={setPageId} setSelected={setSelected} />
          <PanelSection title="Linked typography">
            <Button className="ms-wide" onClick={() => { setCreatingGroup(true); setGroupName(""); setGroupError(""); setIncludeSelection(true); }}>New typography group</Button>
            {creatingGroup && <form className="ms-new-style-group" onSubmit={(e) => {
              e.preventDefault();
              try {
                const result = createStyleGroup(doc, groupName, includeSelection ? selected : []);
                commit(result.doc);
                selectStyleGroup(result.id);
                setCreatingGroup(false);
              } catch (error) { setGroupError(error.message); }
            }}>
              <Field label="New group name"><input autoFocus maxLength={100} value={groupName} onChange={(e) => { setGroupName(e.target.value); setGroupError(""); }} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setCreatingGroup(false); } }} /></Field>
              {selectedTextCount > 0 ? <label className="ms-hint"><input type="checkbox" checked={includeSelection} onChange={(e) => setIncludeSelection(e.target.checked)} /> Include {selectedTextCount} selected text elements</label> : <p className="ms-hint">Create a group, then Shift-click text on the menu to add members.</p>}
              {groupError && <p className="ms-hint" role="alert">{groupError}</p>}
              <div className="ms-style-group-actions"><Button type="submit" disabled={!groupName.trim()}>Create group</Button><Button type="button" onClick={() => setCreatingGroup(false)}>Cancel</Button></div>
            </form>}
            {styleGroupKeys(doc).map((role) => (
                <button
                  className={`ms-role-row ${
                    activeStyleGroup === role ? "is-active" : ""
                  }`}
                  key={role}
                  aria-pressed={activeStyleGroup === role}
                  onClick={() => selectStyleGroup(role)}
                >
                  <span>{styleGroupName(doc, role)}</span>
                  <b>{styleGroupIds(doc, role).length}</b>
                </button>
              ))}
          </PanelSection>
        </>
      )}
    </aside>
  );
}
